import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { products } from '../data/mockData';
import { CheckCircle, ArrowLeft, ArrowRight, Shield, Phone, Star } from 'lucide-react';
import ProductCard from '../components/ProductCard';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const product = products.find(p => p.slug === slug);
  const related = products.filter(p => p.slug !== slug).slice(0, 3);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Produit introuvable</h1>
          <Link to="/nos-stores" className="text-orange-500 hover:underline flex items-center gap-2">
            <ArrowLeft size={16} /> Retour aux stores
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-[#1a1a1a] py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-orange-400 transition-colors">Accueil</Link>
            <span>/</span>
            <Link to="/nos-stores" className="hover:text-orange-400 transition-colors">Nos Stores</Link>
            <span>/</span>
            <span className="text-white">{product.name} – {product.subtitle}</span>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          {/* Image */}
          <div className="relative">
            <div className="rounded-xl overflow-hidden bg-gray-50 border border-gray-100">
              <img
                src={product.image}
                alt={`${product.name} - ${product.subtitle}`}
                className="w-full object-contain h-96"
                onError={(e) => { e.target.src = 'https://images.pexels.com/photos/14212807/pexels-photo-14212807.jpeg?w=800'; }}
              />
            </div>
            <div className="absolute top-4 left-4">
              <span className="bg-orange-500 text-white text-sm font-bold px-3 py-1.5 rounded-full">
                {product.badge}
              </span>
            </div>
          </div>

          {/* Info */}
          <div>
            <Link
              to="/nos-stores"
              className="inline-flex items-center gap-1.5 text-gray-500 hover:text-orange-500 text-sm mb-4 transition-colors"
            >
              <ArrowLeft size={14} /> Retour aux stores
            </Link>

            <h1 className="text-3xl font-black text-gray-900 mb-1">{product.name}</h1>
            <p className="text-orange-500 font-semibold text-lg mb-4">{product.subtitle}</p>

            <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>

            {/* Features */}
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 mb-3 text-sm uppercase tracking-wider">Caractéristiques</h3>
              <div className="grid grid-cols-1 gap-2">
                {product.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle size={16} className="text-orange-500 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Price */}
            <div className="flex items-center gap-4 mb-6 p-4 bg-orange-50 rounded-xl border border-orange-100">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Prix indicatif</p>
                <p className="text-2xl font-black text-gray-900">{product.priceFrom}</p>
                <p className="text-xs text-gray-400 mt-0.5">Prix direct usine • Sur mesure</p>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col gap-3">
              <Link
                to="/contact"
                className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-orange-200 text-base"
              >
                Demander un devis gratuit
                <ArrowRight size={18} />
              </Link>
              <a
                href="tel:0970703038"
                className="flex items-center justify-center gap-2 border-2 border-gray-200 hover:border-orange-300 text-gray-700 font-semibold py-3.5 rounded-xl transition-all duration-200"
              >
                <Phone size={16} />
                Appelez-nous : 09 70 70 30 38
              </a>
            </div>

            {/* Guarantee badge */}
            <div className="mt-4 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Shield size={20} className="text-orange-500" />
              <p className="text-xs text-gray-600">
                Ce produit est garanti <strong>12 ans</strong>. Fabriqué sur mesure dans notre usine
                de Gardanne (13).
              </p>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-14">
            <h2 className="text-2xl font-black text-gray-900 mb-6">Produits similaires</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
